import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiSearch, FiMessageCircle } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { getFAQs } from '../firebase/database';
import SEO from '../components/common/SEO';
import { Spinner } from '../components/common/Loader';

const DEFAULT_FAQS = [
  {
    id: '1', category: 'Orders',
    question: 'How do I place an order?',
    answer: 'You can place an order by browsing our products, adding items to your cart, and proceeding to checkout. We accept guest checkout so you don\'t need an account. You can pay via Cash on Delivery, JazzCash, or EasyPaisa.'
  },
  {
    id: '2', category: 'Orders',
    question: 'Can I cancel or modify my order?',
    answer: 'You can cancel or modify your order within 2 hours of placing it by calling us at +92 300 1234567 or emailing hello@sitsmart.pk. After 2 hours, the order may already be in processing and cannot be modified.'
  },
  {
    id: '3', category: 'Shipping',
    question: 'How long does delivery take?',
    answer: 'Standard delivery takes 5-7 business days. Express delivery takes 2-3 business days. Same-day delivery is available in Karachi only (order by 12 PM). Delivery times may vary during peak seasons.'
  },
  {
    id: '4', category: 'Shipping',
    question: 'Do you deliver across Pakistan?',
    answer: 'Yes! We deliver to all major cities including Karachi, Lahore, Islamabad, Rawalpindi, Faisalabad, Multan, Peshawar, Quetta, Sialkot, Hyderabad, and many more cities across Pakistan.'
  },
  {
    id: '5', category: 'Shipping',
    question: 'How much does shipping cost?',
    answer: 'Standard delivery is PKR 250, Express is PKR 500, and Same-Day (Karachi) is PKR 800. Orders over PKR 5,000 get FREE standard delivery automatically!'
  },
  {
    id: '6', category: 'Orders',
    question: 'What is your return policy?',
    answer: 'All sales are final — we do not accept returns or exchanges. Please check your parcel carefully in front of the rider before paying; once payment is made, no breakage or damage claims can be accepted. If you receive a completely wrong item, contact us within 24 hours with photos.'
  },
  {
    id: '7', category: 'Orders',
    question: 'What if my parcel arrives damaged?',
    answer: 'Always inspect your order before paying the rider. If anything looks damaged, refuse the delivery and contact us immediately — we cannot accept claims after payment has been made for the order.'
  },
  {
    id: '8', category: 'Products',
    question: 'Are your plastic chairs durable?',
    answer: 'Yes! Our plastic chairs are made from high-grade virgin plastic that is UV-resistant, weather-proof, and can hold up to 150kg weight. They come with a 1-year warranty against manufacturing defects.'
  },
  {
    id: '9', category: 'Products',
    question: 'Do products come assembled?',
    answer: 'Most of our plastic chairs come fully assembled. Some products like dining sets and office chairs may require minimal assembly. All necessary tools and instructions are included in the package.'
  },
  {
    id: '10', category: 'Products',
    question: 'Are your baby chairs safe?',
    answer: 'Absolutely! All our baby and kids chairs meet international safety standards. They are made from non-toxic, BPA-free materials with anti-tip features and safety harnesses where applicable.'
  },
  {
    id: '11', category: 'Payment',
    question: 'What payment methods do you accept?',
    answer: 'We accept Cash on Delivery (COD), JazzCash, EasyPaisa, and bank transfers. COD is available across Pakistan. For online payments, your details are always kept secure.'
  },
  {
    id: '12', category: 'Payment',
    question: 'Is Cash on Delivery available?',
    answer: 'Yes! Cash on Delivery is available across all major cities in Pakistan at no extra charge. You pay when your order arrives at your doorstep.'
  },
  {
    id: '13', category: 'Account',
    question: 'Do I need an account to order?',
    answer: 'No! You can checkout as a guest without creating an account. However, creating an account lets you track orders, save your wishlist, and enjoy faster checkout next time.'
  },
  {
    id: '14', category: 'Account',
    question: 'How do I track my order?',
    answer: 'Once your order ships, you\'ll receive an SMS/WhatsApp with tracking information. You can also check your order status in your account under "My Orders" if you have an account.'
  },
  {
    id: '15', category: 'Products',
    question: 'Do you offer bulk/corporate orders?',
    answer: 'Yes! We offer special pricing for bulk orders of 10+ pieces and corporate purchases. Contact us at hello@sitsmart.pk or call +92 300 1234567 to get a custom quote.'
  }
];

const CATEGORIES = ['All', 'Orders', 'Shipping', 'Returns', 'Products', 'Payment', 'Account'];

const FAQItem = ({ faq, index }) => {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className="border border-gray-200 hover:border-[#1a3a6e]/30 transition-colors"
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-6 py-5 text-left"
      >
        <span className="font-semibold text-gray-900 pr-4 text-sm md:text-base">{faq.question}</span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${open ? 'bg-[#1a3a6e] text-white' : 'bg-gray-100 text-gray-500'}`}
        >
          <FiChevronDown className="w-4 h-4" />
        </motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-5 border-t border-gray-100">
              <p className="text-gray-600 text-sm leading-relaxed pt-4">{faq.answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQsPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    getFAQs().then(data => {
      setFaqs(data.length > 0 ? data : DEFAULT_FAQS);
    }).catch(() => {
      setFaqs(DEFAULT_FAQS);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = faqs.filter(f => {
    const matchCat = activeCategory === 'All' || f.category === activeCategory;
    const matchSearch = !search || f.question.toLowerCase().includes(search.toLowerCase()) || f.answer.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const categories = ['All', ...new Set(faqs.map(f => f.category).filter(Boolean))];

  return (
    <>
      <SEO
        title="Frequently Asked Questions"
        description="Find answers to common questions about SitSmart products, shipping, returns, payments, and more."
        keywords="sitsmart faq, furniture questions, shipping pakistan, return policy"
      />

      {/* Hero */}
      <div className="bg-[#0f2347] py-16 relative overflow-hidden">
        <div className="absolute inset-0 diagonal-bg opacity-30" />
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#e63946]/10 rounded-full translate-x-1/3 -translate-y-1/3" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-[#e63946] text-sm font-bold uppercase tracking-widest mb-3">Help Center</p>
            <h1 className="font-display text-5xl font-black text-white mb-4">
              Frequently Asked <span className="text-[#e63946]">Questions</span>
            </h1>
            <p className="text-white/70 text-lg mb-8">Everything you need to know about SitSmart</p>

            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search your question..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-sm outline-none bg-white text-gray-900 shadow-xl"
              />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-14">
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 text-sm font-semibold transition-all duration-200 ${
                activeCategory === cat
                  ? 'bg-[#1a3a6e] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat}
              <span className="ml-1.5 text-xs opacity-70">
                ({cat === 'All' ? faqs.length : faqs.filter(f => f.category === cat).length})
              </span>
            </button>
          ))}
        </div>

        {/* FAQ List */}
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <FiSearch className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-display text-xl font-bold text-gray-700 mb-2">No results found</h3>
            <p className="text-gray-500 text-sm">Try different keywords or browse all categories</p>
            <button onClick={() => { setSearch(''); setActiveCategory('All'); }} className="mt-4 text-[#1a3a6e] text-sm hover:underline">Clear filters</button>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((faq, i) => (
              <FAQItem key={faq.id} faq={faq} index={i} />
            ))}
          </div>
        )}

        {/* Still have questions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-14 bg-[#0f2347] p-8 md:p-12 text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 diagonal-bg opacity-20" />
          <div className="relative">
            <div className="w-14 h-14 bg-[#e63946] flex items-center justify-center mx-auto mb-4">
              <FiMessageCircle className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-display text-2xl font-black text-white mb-2">Still Have Questions?</h3>
            <p className="text-white/70 mb-6 text-sm">Our team is here to help you. We respond within 24 hours.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#e63946] text-white px-6 py-3 font-bold text-sm hover:bg-white hover:text-[#e63946] transition-all duration-300">
                Contact Us
              </Link>
              <a href="https://wa.me/923001234567" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-6 py-3 font-bold text-sm hover:border-white hover:bg-white/10 transition-all duration-300">
                WhatsApp Us
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default FAQsPage;
